import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { CStorageKeys } from '../storage/storage-keys.const';
import { IInitialConfig } from '../venue-config/initial-config.model';
import { ActivatedRouteSnapshot } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { StorageService } from '@core/storage/storage.service';
import { CookieService } from 'ngx-cookie-service';
import { CCookieKeys } from '@core/constants/cookie-constants.const';

@Injectable({
  providedIn: 'root'
})
export class InitialConfigService {
  private initialConfig!: IInitialConfig;
  private initialConfig$: BehaviorSubject<IInitialConfig> = new BehaviorSubject<IInitialConfig>(this.initialConfig);

  private readonly cookieKeys = CCookieKeys;
  private readonly storageKeys = CStorageKeys;
  private readonly rollerUrl = 'roller.app';
  private readonly defaultLangCode = 'en';

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private storageService: StorageService,
    private cookieService: CookieService
  ) {}

  getInitialConfig(): IInitialConfig {
    return this.initialConfig;
  }

  getBaseApiUrl(): string {
    return this.initialConfig.baseApiUrl;
  }

  setInitialConfig(config: IInitialConfig): void {
    this.initialConfig = config;
    this.initialConfig$.next(config);
  }

  /** Called once as the first portion of bootstrap. Returned stream we can handle failure in a similar way to venue config load errors */
  loadInitialConfig(baseApi: string, route: ActivatedRouteSnapshot): Observable<IInitialConfig> {
    let siteKey: string | undefined = this.getSiteKey(route);
    const storedSiteKey = this.storageService.getItem<string>(this.storageKeys.apiKey, false);
    if (this.isSiteKeyExpired(siteKey, storedSiteKey)) {
      this.storageService.clearStorage(false);
      this.storageService.clearTicketUserInfo();
    }

    siteKey = siteKey || storedSiteKey || undefined;
    if (!siteKey) {
      return throwError(() => new Error(`${this.storageKeys.apiKey} not set`));
    }

    this.storageService.setItem(this.storageKeys.apiKey, siteKey, false);

    const checkoutSlug = route.params['slug'];
    this.checkExistingCheckoutSlug(checkoutSlug);
    this.storageService.setItem(this.storageKeys.checkout, checkoutSlug, false);

    /** Set the origin id for the cell */
    const originId = this.cookieService.get(this.cookieKeys.originId);
    if (originId) {
      this.storageService.setItem(this.storageKeys.originId, originId, false);
    }

    const baseApiUrl = `${baseApi}/api`;
    this.storageService.setItem(this.storageKeys.api, baseApiUrl, false);

    return of({
      siteKey,
      checkoutSlug,
      baseApiUrl,
      isCustomDomain: this.isCustomDomain(),
      routeHasVenueSlug: route.data['routeHasVenueSlug'],
      lang: this.defaultLangCode // we'll update this after loading and calculating available translations
    });
  }

  /**
   * Check the incoming checkout slug against a stored checkout slug.
   * Clear all order related storage items if a checkout changes to another during the same browser session
   * @param checkoutSlug route param slug
   */
  private checkExistingCheckoutSlug(checkoutSlug: unknown): void {
    const storedCheckoutSlug = this.storageService.getItem<string>(this.storageKeys.checkout, false);

    if (checkoutSlug && storedCheckoutSlug && checkoutSlug !== storedCheckoutSlug) {
      this.storageService.clearBookingStorage();
    }
  }

  /** If the checkout venue is changed - handle changing checkouts in the same tab */
  private isSiteKeyExpired(siteKey: string | undefined, storedSiteKey: string | null): boolean {
    return !!siteKey && !!storedSiteKey && siteKey !== storedSiteKey;
  }

  /**
   * Gets site key based on
   * 1) the first portion of a roller domain (legacy)
   * 2) is a custom domain
   * 3) has a venue slug query param
   * Defaults to the venue param from the activated route snapshot (first portion of the dynamic url)
   */
  private getSiteKey(route: ActivatedRouteSnapshot): string | undefined {
    const hostname = this.getHostName();

    if (hostname?.includes('.ecom.') && hostname?.includes(this.rollerUrl)) {
      return hostname.substring(0, hostname.indexOf('.'));
    } else if (this.isCustomDomain()) {
      return this.cookieService.get(this.cookieKeys.apiKey);
    } else if (route.queryParams['venue']) {
      return route.queryParams['venue'] as string;
    }

    return route.params['venue'] || undefined;
  }

  /** Returns true if the document hostname does not contain roller.app and valid domain cookie is set */
  private isCustomDomain(): boolean {
    const hostname = this.getHostName();
    return !hostname?.includes(this.rollerUrl) && !!this.cookieService.get(this.cookieKeys.apiKey);
  }

  /** Helper for mocking hostname in unit tests */
  private getHostName(): string | undefined {
    return this.document.location.hostname;
  }
}
